import CryptoJS from 'crypto-js';

const TOKEN_KEY = 'adminToken';
const USER_KEY = 'adminUser';
const LAST_ACTIVITY_KEY = 'adminLastActivityAt';

export const ADMIN_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;

/** Use REACT_APP_SESSION_SECRET in .env for production; never rely on the fallback alone. */
function secretKey() {
  return (
    process.env.REACT_APP_SESSION_SECRET ||
    'GetSettled-Admin-Portal-SessionKey-Set-REACT_APP_SESSION_SECRET'
  );
}

function encrypt(plain) {
  if (plain === null || plain === undefined) return '';
  return CryptoJS.AES.encrypt(String(plain), secretKey()).toString();
}

function decrypt(cipher) {
  if (!cipher) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(cipher, secretKey());
    const str = bytes.toString(CryptoJS.enc.Utf8);
    return str.length ? str : null;
  } catch {
    return null;
  }
}

/**
 * Persist JWT in sessionStorage (encrypted). Survives refresh within the tab; cleared when tab closes.
 */
export function setAdminToken(token) {
  if (token == null || token === '') {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    return;
  }
  sessionStorage.setItem(TOKEN_KEY, encrypt(token));
  markAdminActivity();
}

/**
 * Returns plaintext token or null. Migrates legacy plain localStorage once, then removes it.
 */
export function getAdminToken() {
  if (expireAdminSessionIfIdle()) {
    return null;
  }
  const enc = sessionStorage.getItem(TOKEN_KEY);
  if (enc) {
    const t = decrypt(enc);
    if (t) return t;
  }
  const legacy = localStorage.getItem(TOKEN_KEY);
  if (legacy) {
    setAdminToken(legacy);
    localStorage.removeItem(TOKEN_KEY);
    return legacy;
  }
  return null;
}

export function markAdminActivity() {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, encrypt(Date.now()));
}

export function getAdminLastActivityAt() {
  const enc = sessionStorage.getItem(LAST_ACTIVITY_KEY);
  if (!enc) return null;
  const value = Number(decrypt(enc));
  return Number.isFinite(value) ? value : null;
}

export function isAdminSessionIdleExpired(now = Date.now()) {
  if (!sessionStorage.getItem(TOKEN_KEY) && !localStorage.getItem(TOKEN_KEY)) {
    return false;
  }
  const lastActivityAt = getAdminLastActivityAt();
  if (!lastActivityAt) {
    markAdminActivity();
    return false;
  }
  return now - lastActivityAt >= ADMIN_IDLE_TIMEOUT_MS;
}

export function expireAdminSessionIfIdle(now = Date.now()) {
  if (!isAdminSessionIdleExpired(now)) {
    return false;
  }
  clearAdminSession();
  return true;
}

export function setAdminUser(user) {
  if (!user || typeof user !== 'object') {
    sessionStorage.removeItem(USER_KEY);
    return;
  }
  sessionStorage.setItem(USER_KEY, encrypt(JSON.stringify(user)));
}

/**
 * Returns parsed admin user object. Migrates legacy plain localStorage once, then removes it.
 */
export function getAdminUser() {
  const enc = sessionStorage.getItem(USER_KEY);
  if (enc) {
    const json = decrypt(enc);
    if (json) {
      try {
        return JSON.parse(json);
      } catch {
        return {};
      }
    }
  }
  const raw = localStorage.getItem(USER_KEY);
  if (raw) {
    try {
      const obj = JSON.parse(raw);
      setAdminUser(obj);
      localStorage.removeItem(USER_KEY);
      return obj;
    } catch {
      return {};
    }
  }
  return {};
}

/**
 * Clears all document cookies across root path and domain levels.
 */
export function clearAllCookies() {
  try {
    if (typeof document === 'undefined' || !document.cookie) return;
    const cookies = document.cookie.split(';');
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const domainParts = hostname ? hostname.split('.') : [];

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.slice(0, eqPos).trim() : cookie.trim();
      if (!name) continue;

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      if (hostname) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${hostname};`;
      }

      let domainAcc = '';
      for (let j = domainParts.length - 1; j >= 0; j--) {
        domainAcc = domainParts[j] + (domainAcc ? '.' + domainAcc : '');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domainAcc};`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${domainAcc};`;
      }
    }
  } catch (err) {
    console.error('Failed to clear cookies:', err);
  }
}

/**
 * Clears all Web Cache Storage entries and unregisters active Service Workers.
 */
export function clearAllCaches() {
  try {
    if (typeof window !== 'undefined' && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      }).catch(() => {});
    }
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      }).catch(() => {});
    }
  } catch (err) {
    console.error('Failed to clear caches:', err);
  }
}

/**
 * Completely clears LocalStorage, SessionStorage, Cookies, and Web Caches.
 */
export function clearAllAppStorageAndCache() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.clear();
    }
  } catch (err) {
    console.error('Failed to clear localStorage:', err);
  }

  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.clear();
    }
  } catch (err) {
    console.error('Failed to clear sessionStorage:', err);
  }

  clearAllCookies();
  clearAllCaches();
}

export function clearAdminSession() {
  clearAllAppStorageAndCache();
}

